import type { ComponentProps, ReactElement } from 'react';
import { useContext } from 'react';

import { useTheme } from '@emotion/react';
import {
  AccordionItem as AccessibleAccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
  AccordionItemState,
} from 'react-accessible-accordion';

import { ChevronDownIcon } from '@twistopayments/design-system/icons/inline';

import { Divider } from '../divider';

import { AccordionLastItemContext } from './accordion';
import * as styles from './accordion.styles';

export type AccordionItemProps = {
  noDivider?: boolean;
  children: {
    panel: ReactElement | string;
    startAdornment?: ReactElement;
    heading: string | ReactElement;
  };
} & Omit<ComponentProps<typeof AccessibleAccordionItem>, 'children'>;

export const AccordionItem = ({ children, noDivider, ...props }: AccordionItemProps) => {
  const theme = useTheme();
  const isLast = useContext(AccordionLastItemContext);

  return (
    <>
      <AccessibleAccordionItem {...props}>
        <AccordionItemHeading>
          <AccordionItemButton css={styles.heading(theme)}>
            {children.startAdornment && (
              <div css={[styles.icon, styles.startAdornment(theme)]}>{children.startAdornment}</div>
            )}
            <span className="accordion__item__label" css={styles.label(theme)}>
              {children.heading}
            </span>
            <AccordionItemState>
              {({ expanded }) => (
                <ChevronDownIcon
                  className={expanded ?? false ? 'isExpanded' : ''}
                  css={[styles.icon, styles.chevron(theme)]}
                />
              )}
            </AccordionItemState>
          </AccordionItemButton>
        </AccordionItemHeading>
        <AccordionItemPanel className="accordion__item__panel" css={styles.panel(theme)}>
          {children.panel}
        </AccordionItemPanel>
      </AccessibleAccordionItem>
      {!isLast && !noDivider && <Divider css={styles.horizontalLine(theme)} />}
    </>
  );
};
